import React, { useState } from 'react';
import { useMyProperties } from '../../hooks/useProperties';
import { PropertyForm } from '../Properties/PropertyForm';
import { PropertyCard } from '../Properties/PropertyCard';
import { MultiStepPropertyForm } from './MultiStepPropertyForm';
import { StatusImovel, Imovel } from '../../types/Property';
import './LocadorPage.css';

export const LocadorPage: React.FC = () => {
  const { imoveis, loading, error, deleteProperty, fetchMyProperties } = useMyProperties();

  // Debug: Log dos dados recebidos
  React.useEffect(() => {
    console.log('LocadorPage - imoveis:', imoveis);
    console.log('LocadorPage - loading:', loading);
    console.log('LocadorPage - error:', error);
  }, [imoveis, loading, error]);

  const [showForm, setShowForm] = useState(false);
  const [showMultiStepForm, setShowMultiStepForm] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editingProperty, setEditingProperty] = useState<string | null>(null);
  const [editingImovel, setEditingImovel] = useState<Imovel | null>(null);

  const handleCreateSuccess = () => {
    setShowForm(false);
    setShowMultiStepForm(false);
    setEditingImovel(null);
    // Recarregar a lista de imóveis
    fetchMyProperties();
  };

  const handleEditProperty = (id: string) => {
    const imovel = imoveis.find(i => i.id === id);
    if (imovel) {
      setEditingImovel(imovel);
      setEditingProperty(id);
      setShowMultiStepForm(true);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este imóvel?')) {
      const result = await deleteProperty(id);
      if (result.success) {
        alert('Imóvel excluído com sucesso!');
      } else {
        alert('Erro ao excluir imóvel: ' + result.message);
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setShowMultiStepForm(false);
    setEditingProperty(null);
    setEditingImovel(null);
  };

  const getStatusLabel = (status: StatusImovel) => {
    switch (status) {
      case StatusImovel.PENDENTE:
        return 'Pendente';
      case StatusImovel.DISPONIVEL:
        return 'Disponível';
      case StatusImovel.ALUGADO:
        return 'Alugado';
      case StatusImovel.INATIVO:
        return 'Rejeitado';
      default:
        return status;
    }
  };

  if (showForm) {
    return (
      <div className="locador-page">
        <div className="container">
          <PropertyForm
            onSuccess={handleCreateSuccess}
            onCancel={handleCancelForm}
          />
        </div>
      </div>
    );
  }

        if (showMultiStepForm) {
          return (
            <MultiStepPropertyForm
              onClose={handleCancelForm}
              onSuccess={handleCreateSuccess}
              editingImovel={editingImovel || undefined}
            />
          );
        }

  return (
    <div className="locador-page">
      <div className="container">
        <div className="page-header">
          <h1>Meus Imóveis</h1>
          <p>Gerencie seus imóveis para aluguel</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              onClick={() => setShowMultiStepForm(true)}
              className="btn-primary"
            >
              + Adicionar Novo Imóvel
            </button>
          </div>
        </div>

        {loading && (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Carregando seus imóveis...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>Erro ao carregar imóveis: {error}</p>
            <button onClick={() => window.location.reload()} className="btn-secondary">
              Tentar Novamente
            </button>
          </div>
        )}

        {!loading && !error && imoveis.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              
            </div>
            <h3>Nenhum imóvel cadastrado</h3>
            <p>Comece anunciando seu primeiro imóvel!</p>
            <button
              onClick={() => setShowMultiStepForm(true)}
              className="btn-primary"
            >
              Adicionar Primeiro Imóvel
            </button>
          </div>
        )}

        {!loading && !error && imoveis.length > 0 && (
          <>
            <div className="properties-stats">
              <div className="stat-card">
                <h3>{imoveis.length}</h3>
                <p>Total de Imóveis</p>
              </div>
              <div className="stat-card">
                <h3>{imoveis.filter(i => i.status === StatusImovel.DISPONIVEL).length}</h3>
                <p>Disponíveis</p>
              </div>
              <div className="stat-card">
                <h3>{imoveis.filter(i => i.status === StatusImovel.PENDENTE).length}</h3>
                <p>Pendentes</p>
              </div>
              <div className="stat-card">
                <h3>{imoveis.filter(i => i.status === StatusImovel.INATIVO).length}</h3>
                <p>Rejeitados</p>
              </div>
              <div className="stat-card">
                <h3>{imoveis.filter(i => i.status === StatusImovel.ALUGADO).length}</h3>
                <p>Alugados</p>
              </div>
            </div>

            <div className="properties-grid">
              {imoveis.map(imovel => (
                <div key={imovel.id} className="property-card-wrapper">
                  <PropertyCard imovel={imovel} />
                  
                  {/* Status e Motivo de Rejeição */}
                  <div className="property-status">
                    <div className={`status-badge status-${imovel.status}`}>
                      {getStatusLabel(imovel.status)}
                    </div>
                    {imovel.status === StatusImovel.INATIVO && (imovel as any).motivo_rejeicao && (
                      <div className="rejection-reason">
                        <strong>Motivo da rejeição:</strong>
                        <p>{(imovel as any).motivo_rejeicao}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="property-actions">
                    <button
                      onClick={() => handleEditProperty(imovel.id)}
                      className="btn-edit"
                    >
                       Editar
                    </button>
                    <button
                      onClick={() => handleDeleteProperty(imovel.id)}
                      className="btn-delete"
                    >
                       Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
